import {
  column,
  dateColumn,
  FilterDefs,
  GridColumn,
  GridTableLayout,
  HeaderAction,
  MenuItem,
  multiFilter,
  PageHeaderLayout,
  RowStyles,
  selectColumn,
  simpleDataRows,
  SimpleHeaderAndData,
  toggleFilter,
  useComputed,
  useGridTableApi,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { useMemo } from "react";
import { dateCell, tagCell } from "src/components";
import { Maybe, ScheduleTemplatesFilter, ScheduleTemplateStatus } from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { CreateOrUpdateScheduleTemplateForm } from "src/routes/libraries/schedules/CreateOrUpdateScheduleTemplateModal";
import {
  ScheduleTemplatesDetailFragment,
  useSaveScheduleTemplatesMutation,
  useScheduleTemplatesPageMetadataQuery,
  useScheduleTemplatesQuery,
} from "src/routes/libraries/schedules/ScheduleTemplatesPage.generated";
import { ScheduleType } from "src/routes/projects/schedule-v2/table/ScheduleType";
import { createScheduleTemplateUrl } from "src/RouteUrls";
import { safeEntries, scheduleStatusToTagType, stageCodeToNameMapper } from "src/utils";
import { DuplicateScheduleTemplateModalForm } from "./DuplicateScheduleTemplateModalForm";

type ScheduleTemplateFilterWithArchive = ScheduleTemplatesFilter & { archived: Maybe<boolean> };

export function ScheduleTemplatesPage() {
  const { data } = useScheduleTemplatesPageMetadataQuery();
  const [saveScheduleTemplates] = useSaveScheduleTemplatesMutation();
  const { openModal, closeModal } = useModal();
  const tableApi = useGridTableApi<Row>();

  const selectedTemplates: ScheduleTemplatesDetailFragment[] = useComputed(
    () => tableApi.getSelectedRows("data").map((row) => row.data),
    [tableApi],
  );
  // using mapper here specifically without archive as we don't want to have archived in the dropdown
  const scheduleTemplateStatusToNameMapperWithoutArchive = {
    [ScheduleTemplateStatus.Published]: "Published",
    [ScheduleTemplateStatus.Draft]: "Draft",
  };

  const filterDefs: FilterDefs<ScheduleTemplateFilterWithArchive> = useMemo(
    () => {
      const market = multiFilter({
        label: "Market",
        options: data?.markets || [],
        getOptionLabel: ({ name }) => name,
        getOptionValue: ({ id }) => id,
      });

      const stage = multiFilter({
        label: "Schedule Type",
        options: safeEntries(stageCodeToNameMapper),
        getOptionLabel: ([, name]) => name,
        getOptionValue: ([code]) => code,
      });

      const status = multiFilter({
        label: "Status",
        options: safeEntries(scheduleTemplateStatusToNameMapperWithoutArchive),
        getOptionLabel: ([, name]) => name,
        getOptionValue: ([code]) => code,
      });

      const archived = toggleFilter({ label: "Show Archived" });

      return { market, stage, status, archived };
    },
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-internal-frontend
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const layoutState = useGridTableLayoutState<ScheduleTemplateFilterWithArchive>({
    persistedFilter: { storageKey: "scheduleTemplateFilter", filterDefs },
    search: "client",
  });

  const query = useScheduleTemplatesQuery({ variables: { filter: mapToFilter(layoutState.filter) } });

  const rowStyles: RowStyles<Row> = { data: { rowLink: (row) => createScheduleTemplateUrl(row.data.id) } };

  const scheduleTemplateActionItems: MenuItem[] = useMemo(
    () => {
      // Duplicate applies to exactly one selected template, and that template cannot already be archived.
      const templateToDuplicate =
        selectedTemplates.length === 1 && selectedTemplates[0].status !== ScheduleTemplateStatus.Archived
          ? selectedTemplates[0]
          : undefined;
      const duplicateAction = {
        label: "Duplicate",
        disabled: !templateToDuplicate,
        onClick: () => {
          if (!templateToDuplicate) return;
          openModal({
            content: (
              <DuplicateScheduleTemplateModalForm
                scheduleTemplateId={templateToDuplicate.id}
                stage={templateToDuplicate.stage}
              />
            ),
          });
        },
      };
      const archiveAction = {
        label: "Archive",
        disabled: !selectedTemplates.some((st) => st.status !== ScheduleTemplateStatus.Archived),
        onClick: () =>
          openModal({
            content: (
              <ConfirmationModal
                confirmationMessage={
                  selectedTemplates.length === 1
                    ? "Are you sure you want to archive this template?"
                    : "Are you sure you want to archive these templates?"
                }
                onConfirmAction={async () => {
                  await saveScheduleTemplates({
                    variables: {
                      scheduleTemplates: {
                        scheduleTemplates: selectedTemplates.map((st) => ({
                          id: st.id,
                          status: ScheduleTemplateStatus.Archived,
                        })),
                      },
                    },
                  });
                  await query.refetch();
                  closeModal();
                }}
                label={selectedTemplates.length === 1 ? "Archive template" : "Archive templates"}
                title="Confirm template archiving"
                danger={true}
              />
            ),
          }),
      };
      return [...[archiveAction], ...[duplicateAction]];
    },
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-internal-frontend
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedTemplates],
  );

  const createNewMenuItems: MenuItem[] = [
    {
      label: "Project Template",
      onClick: () => openModal({ content: <CreateOrUpdateScheduleTemplateForm scheduleType={ScheduleType.Project} /> }),
    },
    {
      label: "Development Template",
      onClick: () =>
        openModal({ content: <CreateOrUpdateScheduleTemplateForm scheduleType={ScheduleType.Development} /> }),
    },
  ];
  const headerActions: HeaderAction[] = [
    { kind: "menu", trigger: { label: "Create", variant: "primary" }, items: createNewMenuItems },
  ];
  const tableActions: HeaderAction[] = [
    { kind: "menu", trigger: { icon: "verticalDots" }, items: scheduleTemplateActionItems },
  ];

  return (
    <PageHeaderLayout pageHeader={{ title: "Schedule Templates", actions: headerActions }}>
      <GridTableLayout
        hideEditColumns
        layoutState={layoutState}
        actions={tableActions}
        tableProps={{
          sorting: { on: "client" },
          query,
          columns,
          rowStyles,
          createRows: (data) => simpleDataRows(data?.scheduleTemplates),
          emptyFallback: "No schedule templates found.",
          api: tableApi,
        }}
      />
    </PageHeaderLayout>
  );
}

type Row = SimpleHeaderAndData<ScheduleTemplatesDetailFragment>;

const columns: GridColumn<Row>[] = [
  selectColumn<Row>(),
  column<Row>({ header: "Template Name", data: ({ name }) => name, mw: "240px" }),
  column<Row>({ header: "Stage", data: ({ stage }) => stageCodeToNameMapper[stage], mw: "140px" }),
  column<Row>({ header: "Market", data: ({ market }) => market?.name ?? "--", mw: "140px" }),
  column<Row>({
    header: "Developments",
    data: ({ developments }) => developments.map((m) => m.name).join(", "),
    clientSideSort: false,
    mw: "200px",
  }),
  column<Row>({
    header: "Status",
    data: ({ statusDetail }) => tagCell(scheduleStatusToTagType[statusDetail.code], statusDetail.name),
    mw: "120px",
  }),
  dateColumn<Row>({ header: "Created", data: ({ createdAt }) => dateCell(createdAt), mw: "120px" }),
  dateColumn<Row>({ header: "Last Edited", data: (data) => dateCell(lastEditedDate(data)), mw: "140px" }),
];

function lastEditedDate(data: ScheduleTemplatesDetailFragment) {
  const updatedAts = [data.updatedAt, ...data.schedules.map((s) => s.updatedAt)];
  return updatedAts.maxBy((date) => date.epochMilliseconds);
}

function mapToFilter(filter: ScheduleTemplateFilterWithArchive): ScheduleTemplatesFilter {
  const { archived, status, ...others } = filter;
  return {
    ...others,
    ...(status ? { status } : { status: [ScheduleTemplateStatus.Published, ScheduleTemplateStatus.Draft] }),
    ...(archived && {
      status: [ScheduleTemplateStatus.Archived, ScheduleTemplateStatus.Draft, ScheduleTemplateStatus.Published],
    }),
  };
}
